import { forwardRef, useState } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { ChevronDown, ChevronUp, LoaderCircle, Plus, X } from 'lucide-react';

export function Panel({ title, eyebrow, action, children, className = '' }: { title?: string; eyebrow?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={'panel ' + className}>
      {(title || action) && (
        <header className="panel-header">
          <div>
            {eyebrow && <span className="eyebrow">{eyebrow}</span>}
            {title && <h2>{title}</h2>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function CollapsiblePanel({ title, eyebrow, action, children, className = '', defaultOpen = false }: { title?: string; eyebrow?: string; action?: ReactNode; children: ReactNode; className?: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={'panel collapsible-panel ' + className}>
      <header className="panel-header collapsible-panel-header">
        <button className="collapsible-panel-toggle" type="button" aria-expanded={open} onClick={() => setOpen(value => !value)}>
          <span>
            {eyebrow && <span className="eyebrow">{eyebrow}</span>}
            <span className="collapsible-panel-title">{title}</span>
          </span>
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        {action && <div className="collapsible-panel-action">{action}</div>}
      </header>
      {open && <div className="collapsible-panel-body">{children}</div>}
    </section>
  );
}

export function CollapsibleCard({ eyebrow, title, subtitle, badge, trailing, children, className = '', defaultOpen = false }: { eyebrow?: ReactNode; title: ReactNode; subtitle?: ReactNode; badge?: ReactNode; trailing?: ReactNode; children: ReactNode; className?: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <article className={'record-card collapsible-card ' + className}>
      <button className="collapsible-card-toggle" type="button" aria-expanded={open} onClick={() => setOpen(value => !value)}>
        <span className="collapsible-card-main">
          {eyebrow && <span className="record-type">{eyebrow}</span>}
          <span className="collapsible-card-title">{title}</span>
          {subtitle && <small>{subtitle}</small>}
        </span>
        <span className="collapsible-card-side">
          {badge}
          {trailing}
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {open && <div className="collapsible-card-body">{children}</div>}
    </article>
  );
}

export function Button({ children, variant = 'default', className = '', loading = false, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'danger' | 'ghost'; loading?: boolean }) {
  return (
    <button className={'button ' + variant + ' ' + className} disabled={props.disabled || loading} {...props}>
      {loading && <LoaderCircle className="spin" size={16} />}
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="input" {...props} />;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(props, ref) {
  return <textarea ref={ref} className="input" {...props} />;
});

export function Badge({ children, tone = 'muted' }: { children: ReactNode; tone?: 'good' | 'active' | 'warn' | 'danger' | 'muted' | 'gold' }) {
  return <span className={'badge badge-' + tone}>{children}</span>;
}

export function PageHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </header>
  );
}

export function StatCard({ label, value, detail }: { label: string; value: ReactNode; detail?: string }) {
  return (
    <div className="stat-card">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

export function LoadingBlock({ label = '正在读取数据' }: { label?: string }) {
  if (label === '正在读取数据') return <div className="skeleton-list">{[1,2,3,4].map(i => <div className="skeleton-row" key={i} />)}</div>;
  return <div className="state-block"><LoaderCircle className="spin" size={22} /><span>{label}</span></div>;
}

export function EmptyBlock({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <div className="empty-block"><strong>{title}</strong>{description && <span>{description}</span>}{action && <div className="empty-action">{action}</div>}</div>;
}

export function ActionSheet({ open, title, eyebrow, description, onClose, children }: { open: boolean; title: string; eyebrow?: string; description?: string; onClose: () => void; children: ReactNode }) {
  if (!open) return null;
  return (
    <>
      <button className="sheet-scrim" onClick={onClose} aria-label="关闭表单" />
      <section className="action-sheet" aria-modal="true" role="dialog">
        <header className="action-sheet-head">
          <div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h2>{title}</h2>{description && <p>{description}</p>}</div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="关闭"><X size={20} /></button>
        </header>
        <div className="action-sheet-body">{children}</div>
      </section>
    </>
  );
}

export function Fab({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="fab" onClick={onClick} aria-label={label} title={label}><Plus size={24} /><span>{label}</span></button>;
}

